import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AutoComplete } from 'primereact/autocomplete'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import i18next from 'i18next'
import { getBranches } from '../../store/slices/BusinessSlice'
import { getCostCenter } from '../../store/slices/CostCenterSlice'
import { debounce } from '../../helpers/debounce'
import validSupergitHIS from '../../api/validSupergitHIS'
import SharedButton from '../../components/Shared/SharedButton'
import { convertToArabicNumerals } from '../../uitls/ConvertToArNumber'
import { getSearchedAccounts } from '../../store/slices/AcountsSlice'
import {
  createTransaction,
  getTransactionEntries,
  updateTransaction,
  createTransactionDraft
} from '../../store/slices/TransactionsSlice'

import { InputTextarea } from 'primereact/inputtextarea'
import moment from 'moment'

//Moves the header focus up or down in the grid based on the current header position.
const moveHeaderFocusUpDown = (previousHeader, headerRowCount, isUp) => {
  const previousColumn = previousHeader.column
  const isSpanHeaderHeight =
    !!previousColumn.isSpanHeaderHeight && previousColumn.isSpanHeaderHeight()
  const lastRowIndex = previousHeader.headerRowIndex
  let nextRowIndex = isUp ? lastRowIndex - 1 : lastRowIndex + 1
  let nextColumn
  if (nextRowIndex === -1) {
    return previousHeader
  }
  if (nextRowIndex === headerRowCount) {
    nextRowIndex = -1
  }
  let parentColumn = previousColumn.getParent()
  if (isUp) {
    if (isSpanHeaderHeight) {
      while (parentColumn && parentColumn.isPadding()) {
        parentColumn = parentColumn.getParent()
      }
    }
    if (!parentColumn) {
      return previousHeader
    }
    nextColumn = parentColumn
  } else {
    const children =
      (previousColumn.getChildren && previousColumn.getChildren()) || []
    nextColumn = children.length > 0 ? children[0] : previousColumn
  }
  return {
    headerRowIndex: nextRowIndex,
    column: nextColumn
  }
}

const toNumberOrUndefined = value => {
  if (value === null || value === undefined || value === '') return undefined
  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? undefined : numericValue
}

// Account cell editor component using PrimeReact AutoComplete
const AccountCellEditor = props => {
  const [value, setValue] = useState(props.value || '')
  const lang = i18next.language
  const highlightedIndexRef = useRef(null)
  const [accountSuggestions, setAccountSuggestions] = useState([])
  const [accLoading, setAccLoading] = useState(false)

  // AutoComplete filtering logic
  const handleAccountSearch = event => {
    if (event?.query?.length > 2) {
      debouncedSearchAccoutns(event?.query)
    }
  }

  const debouncedSearchAccoutns = useCallback(
    debounce(async search => {
      try {
        setAccLoading(true)
        const response = await validSupergitHIS.post('searchJournalAccounts', {
          query: search
        })
        const listAccountsValues =
          response?.data && response?.data?.data
            ? Object.entries(response?.data?.data).map(([, account]) => ({
                value: Number(account?.id),
                label:
                  lang === 'ar'
                    ? account?.name_ar + ' ' + account?.code
                    : account?.name_en + ' ' + account?.code,
                code: account?.code,
                name_en: account?.name_en,
                name_ar: account?.name_ar,
                name_code_en: account?.name_code_en,
                name_code_ar: account?.name_code_ar,
                is_vat_out: account?.is_vat_out,
                is_vat_in: account?.is_vat_in
              }))
            : []
        setAccountSuggestions(listAccountsValues)
      } catch (error) {
        console.log(error)
        setAccountSuggestions([])
      } finally {
        setAccLoading(false)
      }
    }, 500),
    [lang]
  )

  const findSuggestionFromValue = useCallback(
    nextValue => {
      if (!nextValue) return null
      if (nextValue?.label) return nextValue

      const textValue = String(nextValue).trim()
      const textCodes = textValue.match(/\d+/g) || []

      return (
        accountSuggestions.find(item => item.label === textValue) ||
        accountSuggestions.find(
          item =>
            item.name_code_en === textValue || item.name_code_ar === textValue
        ) ||
        accountSuggestions.find(item =>
          textCodes.includes(String(item?.code))
        ) ||
        null
      )
    },
    [accountSuggestions]
  )

  const applyAccountSelection = useCallback(
    selectedItem => {
      if (!selectedItem) return false

      const nextRowData = {
        ...props.node?.data,
        account: selectedItem.label,
        accountId: selectedItem?.value || '',
        accountCode: selectedItem?.code,
        accountNameEn: selectedItem?.name_en,
        accountNameAr: selectedItem?.name_ar,
        vat_payable: selectedItem?.is_vat_out || false,
        vat_receiveable: selectedItem?.is_vat_in || false
      }

      setValue(selectedItem)
      props.node.setData(nextRowData)
      props.api.stopEditing({ newValue: selectedItem.label })
      highlightedIndexRef.current = null
      return true
    },
    [props.api, props.node]
  )

  const handleChange = e => {
    setValue(e.value)
    if (applyAccountSelection(findSuggestionFromValue(e?.value))) {
      return
    } else if (!e?.value) {
      props.node.setData({
        ...props.node?.data,
        account: '',
        accountId: '',
        accountCode: '',
        accountNameEn: '',
        accountNameAr: '',
        vat_payable: false,
        vat_receiveable: false
      })
    }
  }

  const handleSelect = e => {
    applyAccountSelection(findSuggestionFromValue(e?.value))
  }

  // Handle keyDown event to navigate and select with arrow keys and Enter
  const handleKeyDown = e => {
    if (e.key === 'ArrowDown') {
      highlightedIndexRef.current =
        highlightedIndexRef.current === null ||
        highlightedIndexRef.current === accountSuggestions.length - 1
          ? 0
          : highlightedIndexRef.current + 1
    } else if (e.key === 'ArrowUp') {
      highlightedIndexRef.current =
        highlightedIndexRef.current === null ||
        highlightedIndexRef.current === 0
          ? accountSuggestions.length - 1
          : highlightedIndexRef.current - 1
    }
  }

  const handleBlur = () => {
    const selectedItem =
      accountSuggestions[highlightedIndexRef.current] ||
      findSuggestionFromValue(value)
    applyAccountSelection(selectedItem)
  }
  return (
    <AutoComplete
      value={value}
      suggestions={accountSuggestions}
      completeMethod={handleAccountSearch}
      field='label'
      onChange={handleChange}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className='w-full'
      forceSelection
      autoFocus
      pt={{
        input: {
          root: {
            className:
              'w-full border-0 !outline-none !shadow-none !py-0 !h-[25px]'
          }
        },
        panel: {
          className:
            '!bg-gray-100 !shadow-lg rounded-[8px] mt-2 pb-2 overflow-hidden'
        },
        loadingIcon: {
          className: `absolute top-[14px] ${
            lang === 'ar' ? '!left-[12px]' : 'right-[12px]'
          } ${accLoading ? '' : 'hidden'}`
        }
      }}
    />
  )
}

// Journal Entries Table Component
const JournalEntriesTable = ({
  date,
  transactionId,
  transactionStatus,
  transactionDescription,
  debitTotal,
  setDebitTotal,
  creditTotal,
  setCreditTotal,
  returnActiveTab = 'journal'
}) => {
  const { t } = useTranslation()
  const lang = i18next.language
  const gridRef = useRef()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const descriptionRef = useRef(null)
  const { branches } = useSelector(state => state.business)
  const { costCenters } = useSelector(state => state.costCenter)
  const { searchedAccountsList } = useSelector(state => state.acount)
  const { entries } = useSelector(state => state.transaction)

  const [isRtl, setIsRtl] = useState(lang === 'ar' || lang === 'he')
  const [rowData, setRowData] = useState([])
  const [columnDefs, setColumnDefs] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const { user } = useSelector(state => state?.auth)
  const initializedRef = useRef(false)
  const navigateToTransactions = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // Get current user's branch name
  const getCurrentBranchName = useCallback(() => {
    if (!user?.branch_id || !branches?.data) return ''
    const currentBranch = Object.values(branches.data).find(
      branch => Number(branch?.id) === Number(user?.branch_id)
    )
    return currentBranch
      ? lang === 'ar'
        ? currentBranch.name_ar
        : currentBranch.name_en
      : ''
  }, [user?.branch_id, branches?.data, lang])

  // Memoize list values to prevent unnecessary re-renders
  const listBranchValues = useMemo(() => {
    return branches?.data
      ? Object.entries(branches?.data).map(([, branch]) => ({
          value: Number(branch?.id),
          label: lang === 'ar' ? branch.name_ar : branch.name_en
        }))
      : []
  }, [branches?.data, lang])

  const listCostCenterValues = useMemo(() => {
    return costCenters?.data
      ? Object.entries(costCenters?.data).map(([, costCenter]) => ({
          value: Number(costCenter?.id),
          label: lang === 'ar' ? costCenter.name_ar : costCenter.name_en
        }))
      : []
  }, [costCenters?.data, lang])

  const listAccountsValues = useMemo(() => {
    return searchedAccountsList?.data
      ? Object.entries(searchedAccountsList?.data).map(([, account]) => ({
          value: Number(account?.id),
          label:
            lang === 'ar'
              ? account?.name_ar + ' ' + account?.code
              : account?.name_en + ' ' + account?.code,
          data: account
        }))
      : []
  }, [searchedAccountsList?.data, lang])

  const resolveAccountFromRow = useCallback(
    rowData => {
      const accountId = rowData?.accountId
      const accountLabel = rowData?.account || ''
      const accountCode = rowData?.accountCode
      const accounts = Object.values(searchedAccountsList?.data || {})

      const matchedById = accountId
        ? accounts.find(acc => Number(acc?.id) === Number(accountId))
        : null

      if (matchedById) {
        return {
          id: Number(matchedById.id),
          code: matchedById.code,
          name_en: matchedById.name_en,
          name_ar: matchedById.name_ar
        }
      }

      if (accountId) {
        return {
          id: Number(accountId),
          code: rowData?.accountCode,
          name_en: rowData?.accountNameEn,
          name_ar: rowData?.accountNameAr
        }
      }

      const matchedListAccount = listAccountsValues.find(
        item => item?.label === accountLabel
      )

      if (matchedListAccount) {
        return {
          id: matchedListAccount.value,
          code: matchedListAccount.data?.code,
          name_en: matchedListAccount.data?.name_en,
          name_ar: matchedListAccount.data?.name_ar
        }
      }

      const normalizedCode = String(accountCode || '').trim()
      const labelCodeMatches = accountLabel.match(/\d+/g) || []
      const possibleCodes = [
        normalizedCode,
        ...labelCodeMatches.map(code => String(code).trim())
      ].filter(Boolean)

      const matchedByCode = accounts.find(acc =>
        possibleCodes.includes(String(acc?.code))
      )

      if (matchedByCode) {
        return {
          id: Number(matchedByCode.id),
          code: matchedByCode.code,
          name_en: matchedByCode.name_en,
          name_ar: matchedByCode.name_ar
        }
      }

      return null
    },
    [listAccountsValues, searchedAccountsList?.data]
  )

  // Calculates the sum of credits and debits cells
  const calculateTotals = useCallback(
    (creditData, debitData) => {
      const totalCredit = creditData.reduce((sum, value) => sum + value, 0)
      const totalDebit = debitData.reduce((sum, value) => sum + value, 0)
      setDebitTotal(totalDebit)
      setCreditTotal(totalCredit)
    },
    [setDebitTotal, setCreditTotal]
  )

  // Convert values to a list of values for agSelectCellEditor
  const branchValues = useMemo(() => {
    return ['', ...listBranchValues.map(item => String(item.label))]
  }, [listBranchValues])

  const costCenterValues = useMemo(() => {
    return ['', ...listCostCenterValues.map(item => String(item.label))]
  }, [listCostCenterValues])

  // Function to handle cell value change
  const handleCellValueChange = params => {
    const creditArray = []
    const debitArray = []
    const { colDef, newValue, node } = params

    // Check if the changed field is either 'creditAmount' or 'debitAmount'
    if (colDef.field === 'creditAmount' || colDef.field === 'debitAmount') {
      gridRef.current?.api.forEachNode(rowNode => {
        const creditAmount = rowNode.data.creditAmount
        const debitAmount = rowNode.data.debitAmount
        if (colDef.field === 'creditAmount') {
          if (newValue && node?.rowIndex === rowNode?.rowIndex) {
            rowNode.setDataValue('debitAmount', 0)
          }
        } else if (colDef.field === 'debitAmount') {
          if (newValue && node?.rowIndex === rowNode?.rowIndex) {
            rowNode.setDataValue('creditAmount', 0)
          }
        }
        // Only push non-zero values
        if (creditAmount && Number(creditAmount) > 0) {
          creditArray.push(Number(creditAmount))
        }
        if (debitAmount && Number(debitAmount) > 0) {
          debitArray.push(Number(debitAmount))
        }
      })
      if (creditArray?.length || debitArray?.length) {
        calculateTotals(creditArray, debitArray)
      } else {
        // Reset totals if no valid amounts
        setDebitTotal(0)
        setCreditTotal(0)
      }
    }
  }

  const columsData = useMemo(
    () => [
      {
        headerName: '',
        valueGetter: 'node.rowIndex + 1',
        width: 50,
        height: 25,
        suppressMenu: true,
        cellRenderer: params => {
          const rowIndex = params.node.rowIndex + 1
          const arabicIndex = convertToArabicNumerals(rowIndex)
          return lang === 'ar' ? arabicIndex : rowIndex
        }
      },
      {
        headerName: '',
        field: 'actions',
        width: 50,
        height: 25,
        suppressMenu: true,
        cellRenderer: 'DeleteButtonRenderer',
        cellClass: 'delete-cell'
      },
      {
        headerName: t('account'),
        field: 'account',
        editable: true,
        height: 25,
        cellEditor: AccountCellEditor,
        cellEditorParams: params => ({
          value: params?.value || ''
        }),
        minWidth: 300,
        flex: 2
      },
      {
        headerName: t('debitAmount'),
        editable: true,
        field: 'debitAmount',
        cellEditor: 'agNumberCellEditor',
        stopEditingWhenCellsLoseFocus: true,
        width: 120,
        height: 25
      },
      {
        headerName: t('creditAmount'),
        field: 'creditAmount',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        stopEditingWhenCellsLoseFocus: true,
        width: 120,
        height: 25
      },
      {
        headerName: t('description'),
        field: 'description',
        editable: true,
        minWidth: 250,
        height: 25,
        flex: 2
      },
      {
        headerName: t('branch'),
        field: 'branch',
        editable: true,
        height: 25,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: branchValues
        },
        cellRenderer: params => {
          const match = listBranchValues.find(
            item => item.label === params.value
          )
          const isLangAr = lang === 'ar'
          return (
            <div>
              <span>
                {match ? match.label : params.value}
                <i
                  className={`pi pi-angle-down absolute top-[6px] text-[12px] ${
                    isLangAr ? 'left-[15px]' : 'right-[15px]'
                  }`}
                ></i>
              </span>
            </div>
          )
        },
        width: 180
      },

      {
        headerName: t('costCenter'),
        field: 'costCenter',
        editable: true,
        height: 25,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: costCenterValues
        },
        cellRenderer: params => {
          const match = listCostCenterValues.find(
            item => item.label === params.value
          )
          const isLangAr = lang === 'ar'
          return (
            <span>
              {match ? match.label : params.value}
              <i
                className={`pi pi-angle-down absolute top-[12px] text-[12px] ${
                  isLangAr ? 'left-[15px]' : 'right-[15px]'
                }`}
              ></i>
            </span>
          )
        },
        width: 180
      },

      {
        field: 'vat_payable',
        hide: true,
        editable: false
      },
      {
        field: 'vat_receiveable',
        hide: true,
        editable: false
      },
      {
        field: 'accountId',
        hide: true,
        editable: false
      },
      {
        field: 'accountCode',
        hide: true,
        editable: false
      },
      {
        field: 'accountNameEn',
        hide: true,
        editable: false
      },
      {
        field: 'accountNameAr',
        hide: true,
        editable: false
      },
      {
        field: 'branchId',
        hide: true,
        editable: false
      },
      {
        field: 'costCenterId',
        hide: true,
        editable: false
      }
    ],
    [
      t,
      lang,
      branchValues,
      costCenterValues,
      listBranchValues,
      listCostCenterValues
    ]
  )

  useEffect(() => {
    if (transactionId) {
      dispatch(getTransactionEntries(transactionId))
    }
  }, [transactionId, dispatch])

  useEffect(() => {
    if (!transactionId) return
    const activeEntries = (entries || []).filter(data => !data?.is_cancelled)
    const formattedRowsEntries = activeEntries?.map(data => {
      let branch =
        listBranchValues?.find(item => item.value === data?.location_id)
          ?.label || ''
      let costCenter =
        listCostCenterValues?.find(item => item.value === data?.cost_center_id)
          ?.label || ''
      return {
        account:
          lang === 'ar'
            ? data?.account_name_ar + ' ' + data?.account_code
            : data?.account_name_en + ' ' + data?.account_code,
        branch,
        branchId: data?.location_id || '',
        costCenter,
        costCenterId: data?.cost_center_id || '',
        accountId: data?.account_id || '',
        accountCode: data?.account_code,
        accountNameEn: data?.account_name_en,
        accountNameAr: data?.account_name_ar,
        debitAmount:
          data.entry_type?.toLowerCase() === 'debit' ? data?.amount : 0,
        creditAmount:
          data.entry_type?.toLowerCase() === 'credit' ? data?.amount : 0,
        description: data?.description ?? '',
        vat_payable: data?.vat_payable || false,
        vat_receiveable: data?.vat_receiveable || false
      }
    })
    const paddedRowsEntries = Array.from(
      { length: 300 },
      (_, index) =>
        formattedRowsEntries[index] || {
          account: '',
          branch: getCurrentBranchName(),
          branchId: user?.branch_id || '',
          costCenter: '',
          costCenterId: '',
          accountId: '',
          debitAmount: 0,
          creditAmount: 0,
          description: '',
          vat_payable: false,
          vat_receiveable: false
        }
    )
    if (formattedRowsEntries && formattedRowsEntries.length > 0) {
      setRowData(paddedRowsEntries)
      // Calculate totals when loading existing entries
      const creditArray = formattedRowsEntries
        .map(entry => Number(entry.creditAmount))
        .filter(amount => amount > 0)
      const debitArray = formattedRowsEntries
        .map(entry => Number(entry.debitAmount))
        .filter(amount => amount > 0)
      if (creditArray.length || debitArray.length) {
        calculateTotals(creditArray, debitArray)
      } else {
        // Reset totals if no valid entries
        setDebitTotal(0)
        setCreditTotal(0)
      }
    } else {
      // Reset totals for new journal entry
      setDebitTotal(0)
      setCreditTotal(0)
    }
    descriptionRef.current.value = transactionDescription ?? ''
  }, [
    entries,
    branches,
    costCenters,
    lang,
    transactionId,
    listBranchValues,
    listCostCenterValues,
    getCurrentBranchName,
    calculateTotals,
    setDebitTotal,
    setCreditTotal,
    transactionDescription
  ])

  useEffect(() => {
    const fetchData = async () => {
      let payload = { query: '' }
      await Promise.all([
        dispatch(getBranches()),
        dispatch(getCostCenter()),
        dispatch(getSearchedAccounts(payload))
      ])
    }
    fetchData()
  }, [dispatch])

  useEffect(() => {
    setIsRtl(lang === 'ar')
    setColumnDefs(columsData)
  }, [lang, columsData])

  // Initialize empty rows ONLY when creating and ONLY once on mount
  useEffect(() => {
    if (!transactionId && branches?.data && !initializedRef.current) {
      initializedRef.current = true
      const currentBranchName = getCurrentBranchName()
      const rowsWithBranch = Array.from({ length: 300 }, () => ({
        account: '',
        debitAmount: 0,
        creditAmount: 0,
        description: '',
        branch: currentBranchName,
        branchId: user?.branch_id || '',
        costCenter: '',
        costCenterId: '',
        accountId: ''
      }))
      setRowData(rowsWithBranch)
      // Reset totals on initial mount
      setDebitTotal(0)
      setCreditTotal(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches?.data])

  const DeleteButtonRenderer = props => {
    if (isPostedOrCancelled) {
      return null
    }

    return (
      <button
        onClick={() => props.context.removeRow(props.node)}
        className='delete-btn'
      >
        ✕
      </button>
    )
  }

  // const gridOptions = {
  //   defaultColDef: {
  //     cellClass:
  //       'border !border-gray-300 !border-t-0 !border-b-0 !border-r-0 py-2',
  //   },
  //   rowHeight: 40,
  //   onCellValueChanged: handleCellValueChange,
  // }

  const gridOptions = {
    defaultColDef: {
      cellClass:
        'border !border-gray-300 !border-t-0 !border-b-0 !border-r-0 py-0 flex items-center'
    },
    rowHeight: 25,
    onCellValueChanged: handleCellValueChange,

    context: {
      removeRow: rowNode => {
        const index = rowNode.rowIndex

        setRowData(prev => {
          const newRows = [...prev]
          newRows.splice(index, 1)

          // Keep rows always = 300 with current branch
          const currentBranchName = getCurrentBranchName()
          while (newRows.length < 300) {
            newRows.push({
              account: '',
              accountId: '',
              debitAmount: 0,
              creditAmount: 0,
              description: '',
              branch: currentBranchName,
              branchId: user?.branch_id || '',
              costCenter: '',
              costCenterId: ''
            })
          }
          const creditArray = []
          const debitArray = []
          newRows.forEach(row => {
            const creditAmount = Number(row.creditAmount) || 0
            const debitAmount = Number(row.debitAmount) || 0
            if (creditAmount > 0) creditArray.push(creditAmount)
            if (debitAmount > 0) debitArray.push(debitAmount)
          })
          calculateTotals(creditArray, debitArray)

          return newRows
        })
      }
    },

    components: {
      DeleteButtonRenderer
    }
  }

  //Navigates between header rows when the Tab key is pressed, supporting both forward and backward navigation.
  const tabToNextHeader = useCallback(params => {
    return moveHeaderFocusUpDown(
      params.previousHeaderPosition,
      params.headerRowCount,
      params.backwards
    )
  }, [])

  // Handles Arrow key navigation between cells (ArrowUp, ArrowDown, ArrowLeft, ArrowRight),
  const navigateToNextCell = useCallback(params => {
    const previousCell = params.previousCellPosition,
      suggestedNextCell = params.nextCellPosition
    let nextRowIndex, renderedRowCount
    switch (params.key) {
      case 'ArrowDown':
        // return the cell above
        nextRowIndex = previousCell.rowIndex + 1
        if (nextRowIndex < -1) {
          return null
        } // returning null means don't navigate
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          rowPinned: previousCell.rowPinned
        }
      case 'ArrowUp':
        // return the cell below
        nextRowIndex = previousCell.rowIndex - 1
        renderedRowCount = params.api.getDisplayedRowCount()
        if (nextRowIndex >= renderedRowCount) {
          return null
        } // returning null means don't navigate
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          rowPinned: previousCell.rowPinned
        }
      case 'ArrowLeft':
      case 'ArrowRight':
        return suggestedNextCell
      default:
        throw Error(
          'this will never happen, navigation is always one of the 4 keys above'
        )
    }
  }, [])

  const getFormattedPayload = () => {
    let dateValue = moment(date).isValid()
      ? moment(date).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
    let descriptionValue = descriptionRef?.current?.value

    const formattedData = []
    gridRef.current?.api.forEachNode(rowNode => {
      const accountLabel = rowNode?.data?.account
      const branchLabel = rowNode?.data?.branch
      const costCenterLabel = rowNode?.data?.costCenter
      const resolvedAccount = accountLabel
        ? resolveAccountFromRow(rowNode?.data)
        : null
      const accountId = resolvedAccount?.id || ''
      const branchId =
        branchLabel &&
        (listBranchValues?.find(item => item.label === branchLabel)?.value ||
          rowNode?.data?.branchId ||
          '')
      const costCenterId =
        costCenterLabel &&
        (listCostCenterValues?.find(item => item.label === costCenterLabel)
          ?.value ||
          rowNode?.data?.costCenterId ||
          '')

      let data = {
        account: accountId,
        accountCode: resolvedAccount?.code || rowNode?.data?.accountCode,
        accountNameEn: resolvedAccount?.name_en || rowNode?.data?.accountNameEn,
        accountNameAr: resolvedAccount?.name_ar || rowNode?.data?.accountNameAr,
        debitAmount: parseFloat(rowNode?.data?.debitAmount) || 0,
        creditAmount: parseFloat(rowNode?.data?.creditAmount) || 0,
        description: rowNode?.data?.description || '',
        branch: branchId,
        costCenter: costCenterId,
        vat_payable: rowNode?.data?.vat_payable || false,
        vat_receiveable: rowNode?.data?.vat_receiveable || false
      }

      if (data?.account) {
        formattedData.push(data)
      }
    })

    const payloadData = {
      date: dateValue,
      description: descriptionValue,
      entry_details: formattedData.map(data => {
        const selectedAccount = Object.values(
          searchedAccountsList?.data || {}
        ).find(acc => Number(acc?.id) === Number(data?.account))
        const selectedBranch = Object.values(branches?.data || {}).find(
          item => Number(item?.id) === Number(data?.branch)
        )
        const selectedCostCenter = Object.values(costCenters?.data || {}).find(
          item => Number(item?.id) === Number(data?.costCenter)
        )

        let entry = {
          account_id: toNumberOrUndefined(data?.account),
          account_code: toNumberOrUndefined(
            selectedAccount?.code || data?.accountCode
          ),
          account_name_en: selectedAccount?.name_en || data?.accountNameEn,
          account_name_ar: selectedAccount?.name_ar || data?.accountNameAr,
          debit_amount: data?.debitAmount
            ? Number(data?.debitAmount?.toFixed(2))
            : 0,
          credit_amount: data?.creditAmount
            ? Number(data?.creditAmount?.toFixed(2))
            : 0,
          description: data?.description,
          vat_payable: data?.vat_payable,
          vat_receiveable: data?.vat_receiveable
        }
        if (data?.branch) {
          entry.branch_id = toNumberOrUndefined(data?.branch)
          entry.branch_name_en = selectedBranch?.name_en
          entry.branch_name_ar = selectedBranch?.name_ar
        }

        if (data?.costCenter) {
          entry.cost_center_id = toNumberOrUndefined(data?.costCenter)
          entry.cost_center_name_en = selectedCostCenter?.name_en
          entry.cost_center_name_ar = selectedCostCenter?.name_ar
        }
        return entry
      })
    }
    return payloadData
  }

  const handleCreateDraft = async () => {
    try {
      if (isSaving) return
      const payloadData = getFormattedPayload()

      if (payloadData.entry_details.length === 0) {
        toast.error(t('Please add at least one entry'))
        return
      }

      setIsSaving(true)
      if (transactionId) {
        const result = await dispatch(
          updateTransaction({
            id: transactionId,
            data: { ...payloadData, journal_entry_id: transactionId }
          })
        )
        if (result?.payload) {
          navigateToTransactions()
        }
        return
      }

      const result = await dispatch(createTransactionDraft(payloadData))
      if (result?.payload?.data?.id) {
        navigateToTransactions()
      }
    } catch (error) {
      console.error('Error creating draft:', error)
      toast.error(t('Failed to save draft'))
    } finally {
      setIsSaving(false)
    }
  }

  const validatePayload = payloadData => {
    if (payloadData.entry_details.length < 2) {
      toast.error(t('Please add at least two entry lines'))
      return false
    }

    const hasDebit = payloadData.entry_details.some(
      entry => Number(entry.debit_amount) > 0
    )
    const hasCredit = payloadData.entry_details.some(
      entry => Number(entry.credit_amount) > 0
    )

    if (!hasDebit || !hasCredit) {
      toast.error(t('Please add at least one debit and one credit line'))
      return false
    }

    const totalDebit = payloadData.entry_details.reduce(
      (sum, entry) => sum + entry.debit_amount,
      0
    )
    const totalCredit = payloadData.entry_details.reduce(
      (sum, entry) => sum + entry.credit_amount,
      0
    )

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(t('Total debit and credit amounts must be equal'))
      return false
    }

    return true
  }

  const handleUpdateOnly = async () => {
    try {
      if (isSaving) return
      const payloadData = getFormattedPayload()

      if (payloadData.entry_details.length === 0) {
        toast.error(t('Please add at least one entry'))
        return
      }

      if (!validatePayload(payloadData)) {
        return
      }

      setIsSaving(true)
      const result = await dispatch(
        updateTransaction({
          id: transactionId,
          data: { ...payloadData, journal_entry_id: transactionId }
        })
      )

      if (result?.payload) {
        navigateToTransactions()
      }
    } catch (error) {
      console.error('Error updating transaction:', error)
      toast.error(t('Failed to submit transaction'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitData = async () => {
    try {
      if (isSaving) return
      const payloadData = getFormattedPayload()

      if (payloadData.entry_details.length === 0) {
        toast.error(t('Please add at least one entry'))
        return
      }

      if (!validatePayload(payloadData)) {
        return
      }

      setIsSaving(true)
      if (transactionId) {
        const updateResult = await dispatch(
          updateTransaction({
            id: transactionId,
            data: { ...payloadData, journal_entry_id: transactionId }
          })
        )

        if (updateResult?.error) {
          return
        }

        // const postResult = await dispatch(
        //   postTransaction({ journal_entry_id: transactionId })
        // )
        if (updateResult?.payload) {
          navigateToTransactions()
        }
      } else {
        const createResult = await dispatch(createTransaction(payloadData))

        // if (!createdId) {
        //   return
        // }

        // const postResult = await dispatch(
        //   postTransaction({ journal_entry_id: createdId })
        // )
        if (createResult?.payload) {
          navigateToTransactions()
        }
      }
    } catch (error) {
      console.error('Error submitting transaction:', error)
      toast.error(t('Failed to submit transaction'))
    } finally {
      setIsSaving(false)
    }
  }

  const isPostedOrCancelled =
    transactionStatus === 'POSTED' || transactionStatus === 'CANCELLED'

  return (
    <React.Fragment>
      <div className='flex justify-between items-center mb-2'>
        <h3 className='text-lg font-semibold'>{t('Journal Entries')}</h3>
      </div>

      <div className='ag-theme-quartz h-[75vh] overflow-y-auto mt-2'>
        <AgGridReact
          ref={gridRef}
          key={isRtl ? 'rtl' : 'ltr'}
          rowData={rowData}
          animateRows={true}
          columnDefs={columnDefs}
          enableRtl={isRtl}
          gridOptions={gridOptions}
          tabToNextHeader={tabToNextHeader}
          navigateToNextCell={navigateToNextCell}
          singleClickEdit={true}
          readOnlyEdit={isPostedOrCancelled}
          suppressClickEdit={isPostedOrCancelled}
          frameworkComponents={{
            DeleteButtonRenderer
          }}
        />
      </div>

      <div className='mt-2'>
        <div className='flex justify-end px-4 py-2 bg-gray-200 rounded-md gap-2'>
          {transactionStatus === 'DRAFT' && transactionId && (
            <SharedButton
              disabled={isSaving}
              onClick={handleUpdateOnly}
              icon='pi pi-save'
              label={t('Update Draft')}
              className='p-button-secondary'
            />
          )}
          <SharedButton
            onClick={navigateToTransactions}
            icon='pi pi-times'
            label={t('Cancel')}
            className='cancel-btn'
          />
          {!transactionId && (
            <SharedButton
              disabled={isSaving}
              onClick={handleCreateDraft}
              icon='pi pi-save'
              label={t('Save Draft')}
              className='p-button-secondary'
            />
          )}
          {!isPostedOrCancelled && (
            <SharedButton
              disabled={isSaving}
              onClick={handleSubmitData}
              icon='pi pi-check'
              label={transactionId ? t('Update') : t('Create')}
              className='submit-btn'
            />
          )}
        </div>
      </div>

      <div className='py-2'>
        <div className='flex flex-col gap-1'>
          <label htmlFor='description' className='text-base font-medium'>
            {t('description')}
          </label>
          <InputTextarea
            id='description'
            placeholder={`${t('enter')} ${t('description')}`}
            type='text'
            ref={descriptionRef}
            disabled={isPostedOrCancelled}
            className='w-full h-[80px] !p-1 rounded-md hover:border-primary !outline-none !shadow-none'
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default JournalEntriesTable
